/**
 * @file PageBgImageFields.tsx
 * @description Поля «Картинки фона» без заголовка (решение пользователя
 * 2026-10-06; `backdrop.image` документа оформления): миниатюра, «Выбрать
 * из медиатеки / Заменить», «Убрать», как расположить, прозрачность 0–90 % и
 * затемнение 0–60 % числом с ползунком (SB120, `NumberSliderField`; прежние
 * ступени — подсказки), «Не показывать на телефоне».
 *
 * Заголовок даёт тот, кто ставит поля: группа «Картинка фона» во вкладке
 * «Фон и рамка» (SB122) или `PageBgImageSection` в фоне страницы.
 * Без картинки — строка-подсказка (пустая группа не прячется, SB122 §6).
 */

import type { SitePageBgImage, SiteTheme } from "@chaiberi/shared";
import { ImagePlus } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { FieldHint } from "../../ui/settings-groups";
import { MediaPickerDialog } from "../media/MediaPickerDialog";
import { CheckRow } from "../CheckRow";
import { Segmented } from "../Segmented";
import { contentMediaUrl } from "../../../lib/api-content-media";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { ownValue } from "../../../lib/site-fields/number-input";
import {
  DIM_FIELD,
  newPageBg,
  opacityOf,
  PAGE_BG_MODE_OPTIONS,
  TRANSPARENCY_FIELD,
  transparencyOf,
} from "../../../lib/site-view/page-bg";

interface Props {
  image: SitePageBgImage | null;
  onBackdrop: (patch: Partial<SiteTheme["backdrop"]>) => void;
}

export function PageBgImageFields({ image, onBackdrop }: Props) {
  const [picking, setPicking] = useState(false);
  const set = (patch: Partial<SitePageBgImage>) => image && onBackdrop({ image: { ...image, ...patch } });

  return (
    <>
      {image ? (
        <img src={contentMediaUrl(image.mediaId)} alt="Картинка фона" className="h-28 w-full max-w-72 rounded-xl bg-surface-low object-cover" />
      ) : (
        <FieldHint className="mt-0">Картинка не выбрана — под блоками только цвет фона страницы.</FieldHint>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setPicking(true)}>
          <ImagePlus aria-hidden />
          {image ? "Заменить" : "Выбрать из медиатеки"}
        </Button>
        {image ? (
          <Button variant="ghost" onClick={() => onBackdrop({ image: null })}>
            Убрать
          </Button>
        ) : null}
      </div>
      {image ? (
        <>
          <div className="flex flex-col gap-2">
            <Label>Как расположить</Label>
            <Segmented aria-label="Как расположить картинку фона" options={PAGE_BG_MODE_OPTIONS} value={image.mode} onChange={(mode) => set({ mode })} />
          </div>
          {/* Умолчание новой картинки — без прозрачности и затемнения (newPageBg). */}
          <NumberSliderField
            label="Прозрачность"
            {...TRANSPARENCY_FIELD}
            value={ownValue(transparencyOf(image.opacity), 0)}
            inherit={{ value: 0, label: "без прозрачности" }}
            onChange={(t) => set({ opacity: opacityOf(t ?? 0) })}
          />
          <NumberSliderField
            label="Затемнение"
            {...DIM_FIELD}
            value={ownValue(image.dim, 0)}
            inherit={{ value: 0, label: "без затемнения" }}
            onChange={(dim) => set({ dim: dim ?? 0 })}
          />
          <CheckRow checked={image.hideOnMobile} onChange={(hideOnMobile) => set({ hideOnMobile })}>
            Не показывать на телефоне
          </CheckRow>
        </>
      ) : null}
      <MediaPickerDialog
        open={picking}
        title="Картинка фона"
        hint="JPEG, PNG или WebP. Большое фото лучше сжать — страница откроется быстрее."
        onClose={() => setPicking(false)}
        onSelect={(media) => {
          setPicking(false);
          onBackdrop({ image: image ? { ...image, mediaId: media.id } : newPageBg(media.id) });
        }}
      />
    </>
  );
}
